import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Star, Loader2 } from "lucide-react";
import { BRAND } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PresentMode() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(() => axios.get(`${API}/admin/plans`, { withCredentials: true }))
      .then((res) => setPlans(res.data))
      .catch(() => navigate("/admin", { replace: true }));
  }, [navigate]);

  if (!plans) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white font-body noise-overlay relative flex flex-col" data-testid="present-mode">
      <header className="flex items-center justify-between px-6 lg:px-10 h-20 border-b border-zinc-800 shrink-0">
        <img src={BRAND.logo} alt="JE Training" className="h-12 w-auto object-contain" />
        <button
          onClick={() => navigate("/admin")}
          data-testid="present-mode-exit"
          className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#D4AF37] text-white px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.14em] transition-colors duration-300"
        >
          <X size={14} /> Exit
        </button>
      </header>

      <main className="flex-1 flex flex-col justify-center px-6 lg:px-10 py-10">
        <div className="text-center mb-10">
          <p className="font-display text-[#D4AF37] text-sm font-bold tracking-[0.3em] uppercase mb-3">
            JE Training — Pricing
          </p>
          <h1 className="font-display text-white font-black uppercase tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl">
            Invest in <span className="text-[#D4AF37]">yourself.</span>
          </h1>
        </div>

        <div
          className={`grid gap-5 max-w-6xl mx-auto w-full ${
            plans.length === 1
              ? "grid-cols-1 max-w-md"
              : plans.length === 2
              ? "grid-cols-1 sm:grid-cols-2 max-w-3xl"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {plans.map((p) => (
            <div
              key={p.id}
              data-testid={`present-plan-${p.id}`}
              className={`relative flex flex-col p-8 lg:p-10 border transition-colors ${
                p.featured
                  ? "border-[#D4AF37] bg-gradient-to-b from-[#D4AF37]/10 to-transparent"
                  : "border-zinc-800 bg-zinc-900/40"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-[#D4AF37] text-zinc-950 px-3 py-1 font-display text-[10px] font-black tracking-[0.2em] uppercase">
                  <Star size={10} fill="#09090B" /> Most Popular
                </span>
              )}
              <h2 className="font-display text-white text-2xl lg:text-3xl font-bold uppercase tracking-tight">
                {p.name}
              </h2>
              <p className="mt-5">
                <span className="font-display text-[#D4AF37] text-6xl lg:text-7xl font-black tracking-tight">
                  £{Number(p.price) % 1 === 0 ? Number(p.price) : Number(p.price).toFixed(2)}
                </span>
              </p>
              <p className="text-zinc-500 text-sm uppercase tracking-[0.16em] mt-1">{p.unit}</p>
              {p.description && (
                <p className="mt-5 text-zinc-400 text-base font-light leading-relaxed">{p.description}</p>
              )}
              {p.features?.length > 0 && (
                <ul className="mt-6 space-y-2.5 border-t border-zinc-800 pt-6">
                  {p.features.map((f, i) => (
                    <li key={i} className="text-zinc-300 text-base font-light flex gap-3">
                      <span className="text-[#D4AF37] font-bold">—</span> {f}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <p className="mt-12 text-center font-display text-zinc-600 text-sm uppercase tracking-[0.3em]">
          Strength · Togetherness
        </p>
      </main>
    </div>
  );
}
